/**
 * Utility functions to disable browser swipe back navigation
 */

/**
 * Disable swipe navigation by preventing only navigation gestures, not scrolling
 */
export const disableSwipeNavigation = () => {
  let startX = 0;
  let startY = 0;
  let startTime = 0;

  // Detect if an element or its parents can scroll horizontally
  const canScrollHorizontally = (element: HTMLElement): boolean => {
    let current = element;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const overflowX = style.overflowX;

      // Check if element can scroll horizontally
      if (
        (overflowX === 'auto' || overflowX === 'scroll') &&
        current.scrollWidth > current.clientWidth
      ) {
        return true;
      }

      // Check for specific scrollable classes
      if (
        current.classList.contains('ant-table-body') ||
        current.classList.contains('ant-table-container') ||
        current.classList.contains('scrollable-container') ||
        current.classList.contains('horizontal-scroll')
      ) {
        return true;
      }

      current = current.parentElement as HTMLElement;
    }
    return false;
  };

  // Prevent only navigation swipe gestures, not content scrolling
  const preventNavigationSwipe = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;

    startX = touch.clientX;
    startY = touch.clientY;
    startTime = Date.now();

    // Only prevent if touch starts very close to the edge (indicating navigation intent)
    // and the target element cannot scroll horizontally
    const target = e.target as HTMLElement;
    const nearLeftEdge = touch.clientX < 20; // Reduced from 50px to 20px for edge gestures
    const nearRightEdge = touch.clientX > window.innerWidth - 20;

    // Don't prevent if the element can scroll horizontally
    if (canScrollHorizontally(target)) {
      return;
    }

    // Only prevent edge gestures that are likely navigation attempts
    if (nearLeftEdge || nearRightEdge) {
      e.preventDefault();
    }
  };

  // Track touch movement to distinguish between navigation and scrolling
  const handleTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;

    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    const deltaTime = Date.now() - startTime;

    // Fast horizontal swipe from edge is likely navigation
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) * 2;
    const isFastSwipe = deltaTime < 300 && Math.abs(deltaX) > 50;
    const isFromEdge = startX < 20 || startX > window.innerWidth - 20;

    const target = e.target as HTMLElement;
    const canScroll = canScrollHorizontally(target);

    // Only prevent if it's a fast horizontal swipe from edge and element can't scroll
    if (isHorizontalSwipe && isFastSwipe && isFromEdge && !canScroll) {
      e.preventDefault();
    }
  };

  // Add event listeners with more precise gesture detection
  document.addEventListener('touchstart', preventNavigationSwipe, { passive: false });
  document.addEventListener('touchmove', handleTouchMove, { passive: false });

  // Add meta tag to improve touch behavior while allowing zoom for accessibility
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=3.0, user-scalable=yes, viewport-fit=cover',
    );
  } else {
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content =
      'width=device-width, initial-scale=1.0, maximum-scale=3.0, user-scalable=yes, viewport-fit=cover';
    document.head.appendChild(meta);
  }

  // Add meta tags to ensure the app can run in standalone mode on mobile browsers
  const webAppMetaTags = [
    { name: 'mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
    { name: 'apple-touch-fullscreen', content: 'yes' },
  ];

  webAppMetaTags.forEach((tag) => {
    const existing = document.querySelector(`meta[name="${tag.name}"]`);
    if (!existing) {
      const meta = document.createElement('meta');
      meta.name = tag.name;
      meta.content = tag.content;
      document.head.appendChild(meta);
    }
  });

  // Set CSS properties on document.documentElement
  const html = document.documentElement;
  html.style.overscrollBehavior = 'none';
  html.style.overscrollBehaviorX = 'none';
  html.style.overflowX = 'hidden';

  // Set CSS properties on body
  document.body.style.overscrollBehavior = 'none';
  document.body.style.overscrollBehaviorX = 'none';
  document.body.style.overflowX = 'hidden';
  document.body.style.touchAction = 'pan-y';

  if (process.env.NODE_ENV === 'development') {
    console.log('Swipe navigation disabled');
  }
};

/**
 * Clean up event listeners (useful for development)
 */
export const enableSwipeNavigation = () => {
  // This would remove the event listeners, but typically not needed in production
  if (process.env.NODE_ENV === 'development') {
    console.log('Swipe navigation enabled (no cleanup implemented)');
  }
};

/**
 * Initialize swipe navigation prevention on DOM ready
 */
export const initDisableSwipeNavigation = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', disableSwipeNavigation);
  } else {
    disableSwipeNavigation();
  }
};
